import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useInfiniteList, makeApiFetcher } from './useInfiniteList.js';

export interface ContactTag {
  id: number;
  slug: string;
  label: string;
  color: string | null;
  count?: number;
}

export interface Contact {
  id: number;
  firstName: string | null;
  lastName: string | null;
  primaryEmail: string | null;
  primaryPhone: string | null;
  businessName: string | null;
  address: string | null;
  website: string | null;
  socialFacebook: string | null;
  socialInstagram: string | null;
  socialTwitter: string | null;
  socialLinkedin: string | null;
  socialYoutube: string | null;
  socialTiktok: string | null;
  category: string;
  source: string;
  relationshipCategory: string | null;
  externalId: string | null;
  notes: string | null;
  firstSeen: string;
  interactionCount: number;
  updatedAt: string;
  lastMessagingActivity: string | null;
  tags?: ContactTag[];
}

export interface ContactsResult {
  rows: Contact[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface ContactFilters {
  search?: string;
  category?: string;
  source?: string;
  relationship?: string;
  tag?: string;
  page?: number;
  limit?: number;
  sortBy?: 'updated' | 'messaging';
}

export function useContacts(filters: ContactFilters = {}) {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.category) params.set('category', filters.category);
  if (filters.source) params.set('source', filters.source);
  if (filters.relationship) params.set('relationship', filters.relationship);
  if (filters.tag) params.set('tag', filters.tag);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));

  return useQuery<ContactsResult>({
    queryKey: ['contacts', filters],
    queryFn: () => api.get<ContactsResult>(`/api/contacts?${params.toString()}`),
  });
}

export function useInfiniteContacts(filters: Omit<ContactFilters, 'page' | 'cursor'> = {}) {
  const params: Record<string, string | boolean | number | undefined> = {};
  if (filters.search) params['search'] = filters.search;
  if (filters.category) params['category'] = filters.category;
  if (filters.source) params['source'] = filters.source;
  if (filters.relationship) params['relationship'] = filters.relationship;
  if (filters.tag) params['tag'] = filters.tag;
  if (filters.limit) params['limit'] = filters.limit;
  if (filters.sortBy) params['sortBy'] = filters.sortBy;

  const fetchFn = makeApiFetcher<Contact>('/api/contacts', params);
  const sortByMessaging = filters.sortBy === 'messaging';

  return useInfiniteList<Contact>(
    ['contacts', 'infinite', filters],
    fetchFn,
    {
      limit: filters.limit ?? 50,
      // El cursor tiene que matchear el mismo campo que ordena el server —
      // '1970...' espejea el COALESCE server-side (listContacts) para que
      // los contactos sin actividad de DM (NULL) sigan paginando al final
      // en vez de desaparecer a partir de la página 2.
      getTimestamp: sortByMessaging
        ? (c) => c.lastMessagingActivity ?? '1970-01-01T00:00:00.000Z'
        : (c) => c.updatedAt,
    },
  );
}

export function useContact(id: number | null) {
  return useQuery<Contact>({
    queryKey: ['contact', id],
    queryFn: () => api.get<Contact>(`/api/contacts/${id}`),
    enabled: id !== null,
  });
}

export function useCategories() {
  return useQuery<Array<{ category: string; count: number }>>({
    queryKey: ['contact-categories'],
    queryFn: () => api.get('/api/contacts/categories'),
    staleTime: 60_000,
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Contact>) => api.post<Contact>('/api/contacts', data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['contacts'] }); },
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<Contact> & { id: number }) =>
      api.put<Contact>(`/api/contacts/${id}`, data),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      void qc.invalidateQueries({ queryKey: ['contact', vars.id] });
    },
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/contacts/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['contacts'] }); },
  });
}

export function useBulkCategorize() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, category }: { ids: number[]; category: string }) =>
      api.post('/api/contacts/bulk/categorize', { ids, category }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      void qc.invalidateQueries({ queryKey: ['contact-categories'] });
    },
  });
}

export function useBulkDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => api.delete('/api/contacts/bulk/delete', { ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      void qc.invalidateQueries({ queryKey: ['contact-categories'] });
    },
  });
}

export function useMergeContacts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ keepId, mergeId }: { keepId: number; mergeId: number }) =>
      api.post('/api/contacts/merge', { keepId, mergeId }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['contacts'] }); },
  });
}

export interface JunkContactItem {
  id: number;
  firstName: string | null;
  lastName: string | null;
  businessName: string | null;
  primaryEmail: string | null;
  primaryPhone: string | null;
  source: string;
  createdAt: string;
  reason: 'no_data' | 'both_invalid';
}

export interface JunkAnalysis {
  total: number;
  items: JunkContactItem[];
  pagination: { limit: number; offset: number; hasMore: boolean };
}

export function useJunkContacts() {
  return useQuery<JunkAnalysis>({
    queryKey: ['contacts-junk-v2'],
    queryFn: () => api.get<JunkAnalysis>('/api/contacts/junk?limit=100&offset=0'),
    staleTime: 0,
  });
}

export interface JunkDeleteResult {
  deleted: number;
  skipped: Array<{ id: number; reason: string }>;
  backupPath: string;
}

export function useDeleteJunkContacts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids?: number[]) =>
      api.post<JunkDeleteResult>('/api/contacts/junk/delete', ids && ids.length > 0 ? { ids } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      void qc.invalidateQueries({ queryKey: ['contacts-junk-v2'] });
    },
  });
}

export function useSetRelationshipCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, category }: { id: number; category: string | null }) =>
      api.put<Contact>(`/api/contacts/${id}/relationship-category`, { category }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      void qc.invalidateQueries({ queryKey: ['contact', vars.id] });
    },
  });
}

// ── Relationship categories ───────────────────────────────────────────────────

export interface RelationshipCategory {
  slug: string;
  labelEs: string;
  labelEn: string;
  color: string;
  isSystem: boolean | number;
  count: number;
}

export function useRelationshipCategories() {
  return useQuery<RelationshipCategory[]>({
    queryKey: ['relationship-categories'],
    queryFn: () => api.get('/api/relationship-categories'),
    staleTime: 30_000,
  });
}

export function useCreateRelationshipCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { slug: string; labelEs: string; labelEn: string; color: string }) =>
      api.post<RelationshipCategory>('/api/relationship-categories', data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['relationship-categories'] }); },
  });
}

export function useDeleteRelationshipCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => api.delete(`/api/relationship-categories/${slug}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['relationship-categories'] });
      void qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useUpdateRelationshipCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, patch }: { slug: string; patch: { labelEs?: string; labelEn?: string; color?: string } }) =>
      api.patch<RelationshipCategory>(`/api/relationship-categories/${slug}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['relationship-categories'] });
      void qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

// ── Contact tags ──────────────────────────────────────────────────────────────

export function useContactTagsList() {
  return useQuery<ContactTag[]>({
    queryKey: ['contact-tags'],
    queryFn: () => api.get('/api/contact-tags'),
    staleTime: 30_000,
  });
}

export function useCreateContactTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { slug: string; label: string; color?: string }) =>
      api.post<ContactTag>('/api/contact-tags', data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['contact-tags'] }); },
  });
}

export function useDeleteContactTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/contact-tags/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['contact-tags'] }); },
  });
}

export function useContactTagAssignments(contactId: number | null) {
  return useQuery<ContactTag[]>({
    queryKey: ['contact-tag-assignments', contactId],
    queryFn: () => api.get<ContactTag[]>(`/api/contacts/${contactId}/tags`),
    enabled: contactId !== null,
    staleTime: 10_000,
  });
}

export function useAssignTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, tagId }: { contactId: number; tagId: number }) =>
      api.post(`/api/contacts/${contactId}/tags`, { tagId }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['contact-tag-assignments', vars.contactId] });
      void qc.invalidateQueries({ queryKey: ['contact-tags'] });
    },
  });
}

export function useRemoveTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, tagId }: { contactId: number; tagId: number }) =>
      api.delete(`/api/contacts/${contactId}/tags/${tagId}`),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['contact-tag-assignments', vars.contactId] });
      void qc.invalidateQueries({ queryKey: ['contact-tags'] });
    },
  });
}

export interface ContactProfile {
  id: number;
  contactId: number;
  isActive: boolean;
  extractedJson: string | null;
  markdownContent: string | null;
  lastUpdatedAt: string | null;
  lastAnalyzedInteractionId: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContactInteraction {
  id: number;
  contactId: number;
  interactionType: string;
  referenceId: number | null;
  summaryText: string;
  metadataJson: string | null;
  createdAt: string;
}

export function useContactProfile(contactId: number | null) {
  return useQuery<ContactProfile | null>({
    queryKey: ['contact-profile', contactId],
    queryFn: () => api.get<ContactProfile | null>(`/api/contacts/${contactId}/profile`),
    enabled: contactId != null && contactId > 0,
  });
}

export function useToggleContactProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, isActive }: { contactId: number; isActive: boolean }) =>
      api.put(`/api/contacts/${contactId}/profile/toggle`, { isActive }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['contact-profile', vars.contactId] });
    },
  });
}

export function useRegenerateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contactId: number) =>
      api.post(`/api/contacts/${contactId}/profile/regenerate`),
    onSuccess: (_data, contactId) => {
      void qc.invalidateQueries({ queryKey: ['contact-profile', contactId] });
    },
  });
}

export function useContactInteractions(
  contactId: number | null,
  params?: { limit?: number; offset?: number; type?: string },
) {
  const p = new URLSearchParams();
  if (params?.limit) p.set('limit', String(params.limit));
  if (params?.offset) p.set('offset', String(params.offset));
  if (params?.type) p.set('type', params.type);

  return useQuery<ContactInteraction[]>({
    queryKey: ['contact-interactions', contactId, params],
    queryFn: () => api.get<ContactInteraction[]>(`/api/contacts/${contactId}/interactions?${p.toString()}`),
    enabled: contactId != null && contactId > 0,
  });
}

export function useAddNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, text }: { contactId: number; text: string }) =>
      api.post(`/api/contacts/${contactId}/notes`, { text }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['contact-interactions', vars.contactId] });
      void qc.invalidateQueries({ queryKey: ['contact-profile', vars.contactId] });
    },
  });
}

// ── Compose context — lookup contact by email ─────────────────────────────────

export interface ContactWithProfile {
  contact: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    primaryEmail: string | null;
    businessName: string | null;
    category: string;
    interactionCount: number;
  };
  profile: {
    markdownContent: string | null;
    isActive: boolean;
  } | null;
}

export function useContactByEmail(email: string) {
  return useQuery<ContactWithProfile>({
    queryKey: ['contact-by-email', email],
    queryFn: () => api.get<ContactWithProfile>(`/api/contacts/by-email?email=${encodeURIComponent(email)}`),
    enabled: !!email && email.includes('@') && email.includes('.'),
    retry: false,
    staleTime: 30_000,
  });
}

// ── Contact categories (managed list) ────────────────────────────────────────

export interface ContactCategory {
  id: number;
  name: string;
  isSystem: boolean | number;
  createdAt: string;
}

export function useContactCategoriesList() {
  return useQuery<ContactCategory[]>({
    queryKey: ['contact-categories-list'],
    queryFn: () => api.get('/api/contact-categories'),
    staleTime: 30_000,
  });
}

export function useCreateContactCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.post<ContactCategory>('/api/contact-categories', { name }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contact-categories-list'] });
      void qc.invalidateQueries({ queryKey: ['contact-categories'] });
    },
  });
}

export function useDeleteContactCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/contact-categories/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['contact-categories-list'] }); },
  });
}

// ── Bulk tag contacts ─────────────────────────────────────────────────────────

export function useBulkTagContacts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, tagId, action }: { ids: number[]; tagId: number; action: 'add' | 'remove' }) =>
      api.post<{ affected: number }>('/api/contacts/bulk/tags', { ids, tagId, action }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      void qc.invalidateQueries({ queryKey: ['contact-tags'] });
    },
  });
}

// ── Contact multi-value emails ────────────────────────────────────────────────

export interface ContactEmailRow {
  id: number;
  contactId: number;
  email: string;
  label: string;
  isPrimary: number;
}

export interface ContactPhoneRow {
  id: number;
  contactId: number;
  phone: string;
  label: string;
  isPrimary: number;
}

export function useContactEmails(contactId: number | null) {
  return useQuery<ContactEmailRow[]>({
    queryKey: ['contact-emails', contactId],
    queryFn: () => api.get<ContactEmailRow[]>(`/api/contacts/${contactId}/emails`),
    enabled: contactId !== null,
  });
}

export function useContactPhones(contactId: number | null) {
  return useQuery<ContactPhoneRow[]>({
    queryKey: ['contact-phones', contactId],
    queryFn: () => api.get<ContactPhoneRow[]>(`/api/contacts/${contactId}/phones`),
    enabled: contactId !== null,
  });
}

export function useAddContactEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, email, label }: { contactId: number; email: string; label?: string }) =>
      api.post<ContactEmailRow>(`/api/contacts/${contactId}/emails`, { email, label }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['contact-emails', vars.contactId] });
      void qc.invalidateQueries({ queryKey: ['contact', vars.contactId] });
    },
  });
}

export function useRemoveContactEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, emailId }: { contactId: number; emailId: number }) =>
      api.delete<{ removed: boolean; newPrimary: string | null }>(`/api/contacts/${contactId}/emails/${emailId}`),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['contact-emails', vars.contactId] });
      void qc.invalidateQueries({ queryKey: ['contact', vars.contactId] });
    },
  });
}

export function useSetPrimaryContactEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, emailId }: { contactId: number; emailId: number }) =>
      api.patch<{ ok: boolean }>(`/api/contacts/${contactId}/emails/${emailId}/set-primary`),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['contact-emails', vars.contactId] });
      void qc.invalidateQueries({ queryKey: ['contact', vars.contactId] });
    },
  });
}

// ── Contact multi-value phones ────────────────────────────────────────────────

export function useAddContactPhone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, phone, label }: { contactId: number; phone: string; label?: string }) =>
      api.post<ContactPhoneRow>(`/api/contacts/${contactId}/phones`, { phone, label }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['contact-phones', vars.contactId] });
      void qc.invalidateQueries({ queryKey: ['contact', vars.contactId] });
    },
  });
}

export function useRemoveContactPhone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, phoneId }: { contactId: number; phoneId: number }) =>
      api.delete<{ removed: boolean; newPrimary: string | null }>(`/api/contacts/${contactId}/phones/${phoneId}`),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['contact-phones', vars.contactId] });
      void qc.invalidateQueries({ queryKey: ['contact', vars.contactId] });
    },
  });
}

export function useSetPrimaryContactPhone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, phoneId }: { contactId: number; phoneId: number }) =>
      api.patch<{ ok: boolean }>(`/api/contacts/${contactId}/phones/${phoneId}/set-primary`),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['contact-phones', vars.contactId] });
      void qc.invalidateQueries({ queryKey: ['contact', vars.contactId] });
    },
  });
}

// ── Timeline consolidado (bitácora) ────────────────────────────────────────────
// Único servicio de línea de tiempo por contacto — todas las fuentes (projects,
// estimates, invoices, email, DM). Antes había dos (este + contactOutreachLog,
// Billing Phase 2 Part E) compitiendo; el segundo se borró. Fuentes nuevas se
// registran en el backend: services/contactTimeline.ts.

export type ContactTimelineEventType =
  | 'email_inbound' | 'email_outbound'
  | 'dm_inbound' | 'dm_outbound'
  | 'lead_outreach_draft' | 'lead_outreach_sent' | 'lead_reply'
  | 'project_outreach_draft' | 'project_outreach_sent' | 'project_reply'
  | 'project_revision' | 'project_client_response'
  | 'estimate_outreach_draft' | 'estimate_outreach_sent' | 'estimate_reply'
  | 'invoice_outreach_draft' | 'invoice_outreach_sent' | 'invoice_reply'
  | 'note_manual' | 'pipeline_stage_change';

export interface ContactTimelineEvent {
  id: string;
  timestamp: number;
  type: ContactTimelineEventType;
  source: 'lead' | 'project' | 'contact' | 'email' | 'dm' | 'estimate' | 'invoice';
  sourceId: string | number;
  title: string;
  summary: string;
  metadata?: Record<string, unknown>;
  deepLink?: string;
}

/** hiddenByModule (2026-08-17): cuántos eventos se ocultaron por tener su
 *  módulo apagado, y de cuál. Se muestra en pantalla — filtrar en silencio es
 *  lo que hace que un cliente crea que perdió datos. */
export interface ContactTimelineResponse {
  events: ContactTimelineEvent[];
  hasMore: boolean;
  hiddenByModule?: Partial<Record<string, number>>;
}

export function useContactTimeline(contactId: number) {
  return useQuery<ContactTimelineResponse>({
    queryKey: ['contact-timeline', contactId],
    queryFn: () => api.get<ContactTimelineResponse>(`/api/contacts/${contactId}/timeline`),
  });
}
